import {
  studentHasCourseAssignment,
  studentEnrollmentForCourseId,
  enrollmentTierForLearningActivity,
} from '@/utils/learningActivityAssignment.js'
import { resolveSeatLicenseType } from '@/components/refRepsPortal/portalOrganization.js'
import { resolvedSeatLicenseTypeFromEnrollment } from '@/utils/courseLicenseAccess.js'

function normalizeId(value) {
  if (value == null || value === '') return null
  return String(value)
}

/** @param {Record<string, unknown> | null | undefined} exam */
export function getExamRequiredCourseIds(exam) {
  const ids = exam?.requiredCourseCompletions
  if (!Array.isArray(ids) || ids.length === 0) return []
  return ids.map(normalizeId).filter(Boolean)
}

/**
 * Whether the learner has this prep course assigned on a Premium seat license.
 *
 * @param {Record<string, unknown> | null | undefined} student
 * @param {string | number | null | undefined} courseId
 */
export function studentHasPremiumEnrollmentForCourse(student, courseId) {
  if (!studentHasCourseAssignment(student, courseId)) return false

  const enrollment = studentEnrollmentForCourseId(student, courseId)
  if (!enrollment) return false

  return enrollmentTierForLearningActivity(enrollment) === 'Premium'
}

/**
 * Whether a learner can receive this exam assignment from an instructor.
 * Requires each prep course in requiredCourseCompletions to be assigned on a Premium seat license.
 *
 * @param {Record<string, unknown> | null | undefined} student
 * @param {Record<string, unknown> | null | undefined} exam
 */
export function studentEligibleForExam(student, exam) {
  if (!student?.info?.id) return false

  const requiredCourseIds = getExamRequiredCourseIds(exam)
  if (!requiredCourseIds.length) return true

  return requiredCourseIds.every((courseId) =>
    studentHasPremiumEnrollmentForCourse(student, courseId)
  )
}

/** @param {unknown[]} students @param {Record<string, unknown> | null | undefined} exam */
export function filterStudentsEligibleForExam(students, exam) {
  return (students || []).filter((student) => studentEligibleForExam(student, exam))
}

/**
 * Whether this org catalog includes the prep course (by id).
 * @param {{ id?: string | number, _id?: string | number }[] | null | undefined} orgCourses
 * @param {string | number | null | undefined} courseId
 */
export function organizationHasExamPrepCourse(orgCourses, courseId) {
  const id = normalizeId(courseId)
  if (!id) return false
  return (orgCourses || []).some((course) => {
    const value = course?.id ?? course?._id ?? course
    return String(value) === id
  })
}

/**
 * Required exam prep course ids that are not on the org catalog.
 * @param {Record<string, unknown> | null | undefined} exam
 * @param {{ id?: string | number, name?: string }[]} [orgCourses]
 */
export function getExamRequiredCoursesMissingFromOrg(exam, orgCourses = []) {
  return getExamRequiredCourseIds(exam).filter(
    (courseId) => !organizationHasExamPrepCourse(orgCourses, courseId)
  )
}

export function examEligibilityMessage(exam) {
  const requiredCourseIds = getExamRequiredCourseIds(exam)
  if (!requiredCourseIds.length) {
    return 'This learner is not eligible to receive this exam.'
  }
  return 'Only learners with all required prep courses assigned on a Premium seat license can receive this exam.'
}

function catalogCourseName(courseId, catalogCourses = []) {
  const course = (catalogCourses || []).find(
    (catalogCourse) => String(catalogCourse?.id) === String(courseId)
  )
  return course?.name || 'a required prep course'
}

function joinNames(names) {
  const unique = [...new Set((names || []).filter(Boolean))]
  if (unique.length === 0) return 'the required prep course'
  if (unique.length === 1) return unique[0]
  if (unique.length === 2) return `${unique[0]} and ${unique[1]}`
  return `${unique.slice(0, -1).join(', ')}, and ${unique[unique.length - 1]}`
}

/**
 * Instructor-facing reason a learner cannot receive this exam, matching group-watch
 * blocked-row fields so the assign UI can show View info + an upgrade modal.
 *
 * @param {Record<string, unknown> | null | undefined} student
 * @param {Record<string, unknown> | null | undefined} exam
 * @param {{ id?: string | number, name?: string }[]} [catalogCourses]
 */
export function describeExamIneligibility(student, exam, catalogCourses = []) {
  const base = {
    eligible: false,
    blockedKind: 'unknown',
    guidanceTitle: 'Unable to assign this exam',
    guidanceDetail: examEligibilityMessage(exam),
    showUpgradeButton: false,
    showUpgradeGuidance: false,
    showAssignButton: false,
    showRequestAccessButton: false,
    learnerLicenseType: null,
    neededLicenseType: 'Premium',
    missingCourseIds: [],
    missingCourseNames: [],
    orgMissingCourseIds: [],
    orgMissingCourseNames: [],
    nonPremiumCourseNames: [],
  }

  if (studentEligibleForExam(student, exam)) {
    return {
      ...base,
      eligible: true,
      blockedKind: null,
      guidanceTitle: 'Eligible',
      guidanceDetail: 'This learner can be assigned this exam.',
    }
  }

  if (!student?.info?.id) {
    base.guidanceDetail = 'This learner record is missing an account id, so they cannot be assigned an exam.'
    return base
  }

  const requiredCourseIds = getExamRequiredCourseIds(exam)
  if (!requiredCourseIds.length) {
    return base
  }

  const missingCourseIds = []
  const missingCourseNames = []
  const orgMissingCourseIds = []
  const orgMissingCourseNames = []
  const nonPremiumCourseNames = []
  let learnerLicenseType = null

  for (const courseId of requiredCourseIds) {
    const name = catalogCourseName(courseId, catalogCourses)
    if (!organizationHasExamPrepCourse(catalogCourses, courseId)) {
      orgMissingCourseIds.push(courseId)
      orgMissingCourseNames.push(name)
      continue
    }
    if (!studentHasCourseAssignment(student, courseId)) {
      missingCourseIds.push(courseId)
      missingCourseNames.push(name)
      continue
    }

    const enrollment = studentEnrollmentForCourseId(student, courseId)
    if (!enrollment || enrollmentTierForLearningActivity(enrollment) !== 'Premium') {
      nonPremiumCourseNames.push(name)
      const rawType = resolvedSeatLicenseTypeFromEnrollment(enrollment)
      learnerLicenseType = resolveSeatLicenseType(rawType || 'Basic')
    }
  }

  base.missingCourseIds = missingCourseIds
  base.missingCourseNames = missingCourseNames
  base.orgMissingCourseIds = orgMissingCourseIds
  base.orgMissingCourseNames = orgMissingCourseNames
  base.nonPremiumCourseNames = nonPremiumCourseNames
  base.learnerLicenseType = learnerLicenseType
  base.neededLicenseType = 'Premium'
  base.showAssignButton = missingCourseIds.length > 0
  base.showRequestAccessButton = orgMissingCourseIds.length > 0

  if (orgMissingCourseNames.length && !missingCourseNames.length && !nonPremiumCourseNames.length) {
    const courses = joinNames(orgMissingCourseNames)
    base.blockedKind = 'org_missing_course'
    base.guidanceTitle = 'Your organization does not have this course'
    base.guidanceDetail =
      `This exam requires ${courses}, but your organization does not have access yet. Request access in the purchasing portal, then you can assign this exam.`
    return base
  }

  if (orgMissingCourseNames.length && missingCourseNames.length && !nonPremiumCourseNames.length) {
    const request = joinNames(orgMissingCourseNames)
    const assign = joinNames(missingCourseNames)
    base.blockedKind = 'org_missing_and_needs_course'
    base.guidanceTitle = 'Course access and assignment required'
    base.guidanceDetail =
      `Your organization does not have access to ${request} — request it in the purchasing portal. They also still need ${assign} assigned after that access is granted.`
    return base
  }

  if (orgMissingCourseNames.length && nonPremiumCourseNames.length) {
    const request = joinNames(orgMissingCourseNames)
    const upgrade = joinNames(nonPremiumCourseNames)
    const current = learnerLicenseType || 'Basic'
    base.blockedKind = 'org_missing_and_upgrade'
    base.guidanceTitle = 'Course access and Premium seat required'
    base.guidanceDetail =
      `Your organization does not have access to ${request} — request it in the purchasing portal. Their in-progress ${upgrade} seat is on ${current} and must be upgraded to Premium before this exam can be assigned.`
    base.showUpgradeButton = true
    base.showUpgradeGuidance = true
    return base
  }

  if (nonPremiumCourseNames.length && !missingCourseNames.length) {
    const courses = joinNames(nonPremiumCourseNames)
    const current = learnerLicenseType || 'Basic'
    base.blockedKind = 'license_mismatch'
    base.guidanceTitle = 'Seat license needs an upgrade'
    base.guidanceDetail =
      `This learner's in-progress ${courses} seat is on ${current}, but practice exams require Premium. Upgrade their in-progress course seat from the purchasing portal, then assign this exam.`
    base.showUpgradeButton = true
    base.showUpgradeGuidance = true
    return base
  }

  if (missingCourseNames.length && !nonPremiumCourseNames.length) {
    const courses = joinNames(missingCourseNames)
    base.blockedKind = 'needs_course'
    base.guidanceTitle = 'Needs the required prep course assigned'
    base.guidanceDetail =
      `This exam requires ${courses} on a Premium seat. Use Assign course to add it to their account, then you can assign this exam.`
    return base
  }

  if (missingCourseNames.length && nonPremiumCourseNames.length) {
    const missing = joinNames(missingCourseNames)
    const upgrade = joinNames(nonPremiumCourseNames)
    const current = learnerLicenseType || 'Basic'
    base.blockedKind = 'needs_course_and_upgrade'
    base.guidanceTitle = 'Prep course and Premium seat required'
    base.guidanceDetail =
      `They still need ${missing} assigned — use Assign course here. Their in-progress ${upgrade} seat is on ${current} and must be upgraded to Premium in the purchasing portal before this exam can be assigned.`
    base.showUpgradeButton = true
    base.showUpgradeGuidance = true
    return base
  }

  return base
}

/**
 * RefReps practice exam when instructor-assigned or not tied to a state association.
 * @param {Record<string, unknown> | null | undefined} exam
 */
export function isRefRepsPracticeExam(exam) {
  if (!exam) return false
  if (exam.isInstructorAssignmentExam === true) return true

  const stateAssociations = exam.stateAssociations
  return !Array.isArray(stateAssociations) || stateAssociations.length === 0
}

/** @param {Record<string, unknown> | null | undefined} exam */
export function isStateExam(exam) {
  return !isRefRepsPracticeExam(exam)
}

/** Whether the learner has already started or completed this exam attempt. */
export function examAssignmentIsConsumed(assignment) {
  if (!assignment) return false
  if (assignment.hasStarted === true) return true
  const status = String(assignment.status || '').toLowerCase()
  return status === 'started' || status === 'completed'
}

/** Whether this exam assignment was made by the given organization. */
export function examAssignmentAssignedByOrganization(assignment, organizationId) {
  const orgId = organizationId == null || organizationId === '' ? null : String(organizationId)
  if (!orgId) return false

  const assignedOrgId = assignment?.assignedBy?.companyID
  if (assignedOrgId == null || assignedOrgId === '') return false

  return String(assignedOrgId) === orgId
}

/** Whether an instructor from this org may remove the assignment (not started/consumed). */
export function canUnassignExamAssignment(assignment, organizationId) {
  if (!assignment || examAssignmentIsConsumed(assignment)) return false
  return examAssignmentAssignedByOrganization(assignment, organizationId)
}

/** Normalize POST /get-student-assigned-exam-data success shapes to a row array. */
export function normalizeAssignedExamDataResponse(data) {
  const rows = Array.isArray(data) ? data : (data?.students ?? [])
  return rows.map((row) => normalizeAssignedExamRow(row))
}

/**
 * Map an assigned exam record (API or assign-user-exam) for the instructor assign UI.
 * @param {Record<string, unknown>} row
 * @param {Record<string, unknown> | null | undefined} [student]
 */
export function normalizeAssignedExamRow(row, student) {
  if (!row || typeof row !== 'object') return row

  const status = String(row.status || 'not_started').toLowerCase()
  return {
    ...row,
    id: row.exam_id ?? row.id,
    exam_id: row.exam_id,
    user_id: row.user_id,
    user_email: row.user_email,
    user_name: student?.info?.name || row.user_name || student?.user_name || '',
    profileImage: student?.info?.profileImage || row.profileImage || student?.profileImage || '',
    state_exam_id: row.state_exam_id,
    title: row.title,
    sport: row.sport,
    status: row.status,
    hasStarted: row.hasStarted === true || (status !== 'not_started' && status !== ''),
    assignedBy: row.assignedBy,
    totalQuestions: row.totalQuestions,
    stateImage: row.stateImage,
  }
}
